/**
 * KitchenTicketsPage: kitchen tickets, newest first (FR-ORD-20, FR-KDS-01, ADR 0027), on ListTable with the side filter
 * panel (station, status, print status, fired date range, and at HO the store) and a search by order number or table.
 *
 * Props: `mode` ('store' polls every 15 seconds while the tab is visible; 'ho' shows finished orders only, with the
 * lag notice, and reloads every minute). A row opens `/kitchen/tickets/:id`; the order number links to the order.
 */
import { Link as CarbonLink, Tag } from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ListTable, type ListColumn } from '../components/ListTable';
import { PageHeader } from '../components/PageHeader';
import { DateRangeFilter } from '../fields/DateField';
import { useReferenceOptions } from '../fields/references';
import { FilterLayout } from '../filters/FilterPanel';
import { ChoiceDropdown } from '../filters/ResourceFilters';
import { useFilters } from '../filters/useFilters';
import { dateRange, useFormat } from '../orders/model';
import { LagNotice } from '../overview/widgets';
import { asProblem, problemMessage } from '../problems';
import type { KitchenTicketSummary } from './kitchenApi';
import {
  KITCHEN_POLL_MS,
  PRINT_STATUSES,
  TICKET_STATUSES,
  kitchenKey,
  useKitchenApi,
  type KitchenMode,
} from './model';
import { useStationOptions } from './stations';
import { KitchenStatusTag } from './KitchenStatusTag';
import './kitchen.scss';

const PAGE_SIZES = [25, 50, 100];

export function KitchenTicketsPage({ mode }: { mode: KitchenMode }) {
  const { t } = useTranslation();
  const api = useKitchenApi();
  const navigate = useNavigate();
  const format = useFormat();
  const stores = useReferenceOptions(mode === 'ho' ? 'org.store' : undefined);
  const stations = useStationOptions(mode);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0] ?? 25);
  const filters = useFilters({ station: '', status: '', printStatus: '', storeId: '', from: '', to: '' }, () => setPage(1));
  const { station, status, printStatus, storeId, from, to } = filters.applied;

  const params = {
    station: station || undefined,
    status: status || undefined,
    printStatus: printStatus || undefined,
    storeId: storeId || undefined,
    search: search || undefined,
    ...dateRange(from, to),
    page,
    pageSize,
  };
  const list = useQuery({
    queryKey: [...kitchenKey, 'tickets', params],
    queryFn: () => api.tickets(params),
    placeholderData: keepPreviousData,
    refetchInterval: mode === 'store' ? KITCHEN_POLL_MS : 4 * KITCHEN_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const storeName = (id: string) => stores.data?.find((s) => s.value === id)?.label ?? '';

  const columns: ListColumn<KitchenTicketSummary>[] = [
    {
      key: 'ticketNumber',
      header: t('admin.kitchen.columns.ticket'),
      sortable: true,
      render: (k) => `${k.stationCode} #${k.ticketNumber}`,
      sortValue: (k) => Number(k.ticketNumber),
    },
    ...(mode === 'ho'
      ? [
          {
            key: 'store',
            header: t('admin.kitchen.columns.store'),
            render: (k: KitchenTicketSummary) => storeName(k.storeId),
            sortValue: (k: KitchenTicketSummary) => storeName(k.storeId),
          },
        ]
      : []),
    { key: 'stationName', header: t('admin.kitchen.columns.station'), sortable: true },
    {
      key: 'orderNumber',
      header: t('admin.kitchen.columns.order'),
      sortable: true,
      render: (k) => (
        <CarbonLink as={Link} to={`/orders/${k.orderId}`} onClick={(e: { stopPropagation(): void }) => e.stopPropagation()}>
          {k.orderNumber}
        </CarbonLink>
      ),
    },
    { key: 'headerLabel', header: t('admin.kitchen.columns.label') },
    { key: 'saleTypeCode', header: t('admin.kitchen.columns.saleType') },
    {
      key: 'itemCount',
      header: t('admin.kitchen.columns.items'),
      className: 'pos-admin-page__number',
      render: (k) => format.number(k.itemCount),
    },
    {
      key: 'isAddition',
      header: t('admin.kitchen.columns.addition'),
      render: (k) => (k.isAddition ? <Tag type="gray">{t('admin.kitchen.addition')}</Tag> : null),
    },
    { key: 'status', header: t('admin.kitchen.columns.status'), render: (k) => <KitchenStatusTag kind="ticket" status={k.status} /> },
    ...(mode === 'store'
      ? [
          {
            key: 'printStatus',
            header: t('admin.kitchen.columns.printStatus'),
            render: (k: KitchenTicketSummary) => <PrintStatus ticket={k} />,
          },
        ]
      : []),
    {
      key: 'firedAt',
      header: t('admin.kitchen.columns.firedAt'),
      sortable: true,
      render: (k) => format.dateTime(k.firedAt),
      sortValue: (k) => k.firedAt,
    },
  ];

  const choice = <T extends string>(kind: string, values: readonly T[]) =>
    values.map((v) => ({ id: v, label: t(`admin.kitchen.${kind}.${v}` as I18nKey) }));

  return (
    <section className="pos-admin-page">
      <PageHeader
        title={t('admin.kitchen.tickets.title')}
        description={t(mode === 'ho' ? 'admin.kitchen.tickets.hoHint' : 'admin.kitchen.tickets.hint')}
        actions={mode === 'ho' ? <LagNotice /> : undefined}
      />
      <FilterLayout
        filters={filters}
        fields={
          <>
            <ChoiceDropdown
              id="kitchen-station"
              label={t('admin.kitchen.filters.station')}
              choices={stations}
              value={filters.draft.station}
              onChange={(v) => filters.set('station', v)}
            />
            <ChoiceDropdown
              id="kitchen-status"
              label={t('admin.kitchen.filters.status')}
              choices={choice('ticket', TICKET_STATUSES)}
              value={filters.draft.status}
              onChange={(v) => filters.set('status', v)}
            />
            {mode === 'store' && (
              <ChoiceDropdown
                id="kitchen-print-status"
                label={t('admin.kitchen.filters.printStatus')}
                choices={choice('print', PRINT_STATUSES)}
                value={filters.draft.printStatus}
                onChange={(v) => filters.set('printStatus', v)}
              />
            )}
            {mode === 'ho' && (
              <ChoiceDropdown
                id="kitchen-store"
                label={t('admin.kitchen.filters.store')}
                choices={(stores.data ?? []).map((s) => ({ id: s.value, label: s.label }))}
                value={filters.draft.storeId}
                onChange={(v) => filters.set('storeId', v)}
              />
            )}
            <DateRangeFilter
              id="kitchen"
              legendText={t('admin.kitchen.filters.firedAt')}
              fromLabel={t('admin.filters.from')}
              toLabel={t('admin.filters.to')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(range) => {
                filters.set('from', range.from);
                filters.set('to', range.to);
              }}
            />
          </>
        }
      >
        <ListTable
          ariaLabel={t('admin.kitchen.tickets.title')}
          columns={columns}
          rows={list.data?.items ?? []}
          loading={list.isLoading}
          error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
          onRetry={() => void list.refetch()}
          search={{
            label: t('admin.kitchen.tickets.search'),
            onChange: (value) => {
              setSearch(value);
              setPage(1);
            },
          }}
          filters={filters}
          onRowClick={(k) => void navigate(`/kitchen/tickets/${k.id}`)}
          rowLabel={(k) => `${k.stationCode} #${k.ticketNumber}`}
          empty={{
            heading: t('admin.kitchen.tickets.empty'),
            description: mode === 'ho' ? t('admin.kitchen.tickets.hoEmpty') : undefined,
          }}
          pagination={{
            page,
            pageSize,
            pageSizes: PAGE_SIZES,
            total: list.data?.total ?? 0,
            capped: list.data?.totalCapped,
            onChange: ({ page: nextPage, pageSize: nextSize }) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      </FilterLayout>
    </section>
  );
}

/** A ticket's print state, with a marker when the fallback printer took it. */
export function PrintStatus({ ticket }: { ticket: KitchenTicketSummary }) {
  const { t } = useTranslation();
  return (
    <span className="pos-kitchen__tags">
      <KitchenStatusTag kind="print" status={ticket.printStatus} />
      {ticket.printedFallback && <Tag type="gray">{t('admin.kitchen.fallback')}</Tag>}
    </span>
  );
}
